/**
 * Resolve a plugin component by entrypoint, shared by dashboard widgets and
 * embeddable components:
 *
 *   "File#Export" → ui/pages|components/File.tsx, export Export
 *   "Name"        → ui/pages|components/Name.tsx, export Name (or default)
 *                 → else a named export of the plugin's ui/index.ts
 */

import type React from 'react';

export type ComponentLoader = () => Promise<React.ComponentType<any>>;

export function isComponentLike(value: unknown): value is React.ComponentType<any> {
  if (typeof value === 'function') return true;
  // memo / forwardRef / lazy objects
  return !!value && typeof value === 'object' && typeof (value as { $$typeof?: unknown }).$$typeof === 'symbol';
}

function pick(mod: Record<string, unknown>, exportName: string, fallbackToDefault: boolean): React.ComponentType<any> {
  const value = mod[exportName] ?? (fallbackToDefault ? mod.default : undefined);
  if (!isComponentLike(value)) throw new Error(`Export "${exportName}" is not a component`);
  return value;
}

export function resolveComponentLoader(
  entrypoint: string,
  fileLoaders: Record<string, () => Promise<any>>,
  uiModule?: Record<string, unknown> | null,
): ComponentLoader | null {
  const hash = entrypoint.indexOf('#');
  if (hash >= 0) {
    const file = entrypoint.slice(0, hash);
    const exportName = entrypoint.slice(hash + 1);
    const loader = fileLoaders[file];
    return loader ? () => loader().then((m) => pick(m, exportName, false)) : null;
  }
  const loader = fileLoaders[entrypoint];
  if (loader) return () => loader().then((m) => pick(m, entrypoint, true));
  const fromIndex = uiModule?.[entrypoint];
  if (isComponentLike(fromIndex)) return async () => fromIndex;
  return null;
}
